




























































































































































































/* General */
import Vue from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import EmailClientFigure from '@/components/global/email-client-figure.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesOutputOutgoingEmailSettings } from '@/api/types/response.types'
import { IMailboxesInputOutgoingFromAddress } from '@/api/types/request.types'

export default Vue.extend({
  name: 'MailboxSettingsOutgoingEmail',

  metaInfo(): any {
    return {
      title: `Outgoing Email - ${this.formData.name}`,
    }
  },

  components: {
    MailboxSettingsLayout,
    EmailClientFigure,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      isRefreshingOutgoingEmailSettings: false,
      fromDialogVisible: false,
      showDnsRecords: false,

      pollingForOutgoingEmailConfirmation: false,
      poller: null,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 36,

      postmarkSenderSignatureConfirmationUrl: '',
      postmarkUrlClicked: false,

      forwardingEmailAddresses: [] as string[],

      dialogData: {
        fromName: '',
        fromAddress: '',
      },

      formData: {
        name: '',
      } as IMailboxesOutputOutgoingEmailSettings,
      formRules: {
        fromName: [
          { required: true, message: 'Please add a name', trigger: 'submit' },
        ],
        fromAddress: [
          {
            required: true,
            type: 'email',
            message: 'Please enter a valid e-mail address',
            trigger: 'submit',
          },
        ],
      },
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    defaultOutgoingFromAddress(): string {
      return process.env.VUE_APP_DEFAULT_OUTGOING_FROM_ADDRESS
    },
    hasPendingEmail(): boolean {
      return this.formData.pendingOutgoingFromEmail !== null
    },
    hasCustomFromAddress(): boolean {
      return this.formData.dnsInfo !== null
    },
    hasNotAttemptedCustomFrom(): Boolean {
      return !this.hasPendingEmail && !this.hasCustomFromAddress
    },
    isPendingEmailAForwardingAddress(): boolean {
      return (
        this.formData.pendingOutgoingFromEmail !== null &&
        this.forwardingEmailAddresses.includes(
          this.formData.pendingOutgoingFromEmail.emailAddress,
        )
      )
    },
    hasPostmarkSenderSignatureConfirmationUrl(): boolean {
      return this.postmarkSenderSignatureConfirmationUrl !== ''
    },
  },

  watch: {
    fromDialogVisible(newVal) {
      // open modal
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.fromForm_fromName.focus()
        })
      }
    },
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    await Promise.all([
      this.getOutgoingEmailSettings(),
      this.getAutoForwarding(),
    ])
    this.isLoading = false
  },

  beforeDestroy() {
    this.endPolling()
  },

  methods: {
    endPolling() {
      clearInterval(this.poller)
      this.pollingForOutgoingEmailConfirmation = false
    },
    clearPostmarkUrlInfo() {
      this.postmarkSenderSignatureConfirmationUrl = ''
      this.postmarkUrlClicked = false
    },
    async pollForOutgoingEmailConfirmation() {
      // Clear just in case there's any rif-raff...
      this.endPolling()
      this.clearPostmarkUrlInfo()
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.confirmEmail()
      }, this.pollIntervalInMS)
      this.pollingForOutgoingEmailConfirmation = true
    },
    async getOutgoingEmailSettings() {
      try {
        this.isRefreshingOutgoingEmailSettings = true
        this.formData = await MailboxesAPI.get_OutgoingEmailSettings(this.id)
        this.dialogData.fromName = this.formData.outgoingFromEmail.name
        this.dialogData.fromAddress = this.formData.outgoingFromEmail.emailAddress
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isRefreshingOutgoingEmailSettings = false
      }
    },
    async getAutoForwarding() {
      try {
        const response = await MailboxesAPI.get_AutoForwarding(this.id)
        this.forwardingEmailAddresses = response.listForwardingEmailAddresses
      } catch (error) {
        this.$error.emit(error)
      }
    },
    submitFromForm() {
      // @ts-ignore
      this.$refs.fromForm.validate(async (valid: boolean) => {
        if (valid) {
          try {
            this.isSubmitting = true
            const data = {
              fromName: this.dialogData.fromName,
              fromEmailAddress: this.dialogData.fromAddress,
            } as IMailboxesInputOutgoingFromAddress
            const response = await MailboxesAPI.post_OutgoingFromAddress(
              this.id,
              data,
            )
            this.$messaging.showSuccess(response.message)
            this.fromDialogVisible = false
          } catch (err) {
            this.$error.emit(err, true)
          } finally {
            this.isSubmitting = false
            await this.getOutgoingEmailSettings()
            if (this.hasPendingEmail) {
              await this.pollForOutgoingEmailConfirmation()
            }
          }
        } else {
          return false
        }
      })
    },
    async resendVerificationEmail() {
      try {
        const response = await MailboxesAPI.get_ResendOutgoingFromAddressVerification(
          this.id,
        )
        this.$messaging.showSuccess(response.message)
        this.pollForOutgoingEmailConfirmation()
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async confirmEmail() {
      try {
        // See if there's a Postmark link to click (which would then let the
        // following confirmation call succeed)
        if (!this.hasPostmarkSenderSignatureConfirmationUrl) {
          await this.retrievePostmarkSenderSignatureConfirmationUrl()
        }
        // If still pending, will throw error...
        await MailboxesAPI.post_ConfirmPendingOutgoingFromAddress(this.id)
        // If passes, can assume email was confirmed...
        await this.getOutgoingEmailSettings()
        this.endPolling()
      } catch (error) {
        // Don't emit anything...this is a polling method
      }
    },
    async retrievePostmarkSenderSignatureConfirmationUrl() {
      const response = await MailboxesAPI.get_PostmarkSenderSignatureConfirmationUrl(
        this.id,
      )

      if (response.url && response.url.length > 0) {
        this.postmarkSenderSignatureConfirmationUrl = response.url
      }
    },
  },
})
